import { useEffect, useState, useRef } from 'react';
import API_URL from '../../apiConfig';

function Parametres() {
  const [adminInfo, setAdminInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [errorUpdate, setErrorUpdate] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const fileInputRef = useRef(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);

  // Champs du formulaire
  const [formData, setFormData] = useState({
    idUser: "",
    prenom: "",
    nom: "",
    identifiant: "",
    mdp: ""
  });

  useEffect(() => {
    document.title = "LAMP | Admin - Paramètres";
    const fetchInfosAdmin = async () => {
      try {
        setLoading(true);
        const res = await fetch(`${API_URL}/admin/infosadmin`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "include"
        });
        const data = await res.json();
        if (data.success && data.infosAdmin.length > 0) {
          const info = data.infosAdmin[0];
          // Gestion robuste de la casse des colonnes de la DB
          const safeId = info.idUser || info.iduser;
          const safePrenom = info.prenom || "";
          const safeNom = info.nom || "";
          const safeIdentifiant = info.identifiant || "";
          const safeMdp = info.mdp || "";

          setAdminInfo(info);
          setFormData({
            idUser: safeId,
            prenom: safePrenom,
            nom: safeNom,
            identifiant: safeIdentifiant,
            mdp: safeMdp
          });
        } else {
          setError("Impossible de charger vos informations de profil.");
        }
      } catch (err) {
        setError("Erreur serveur lors du chargement du profil.");
      } finally {
        setLoading(false);
      }
    };
    fetchInfosAdmin();
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleFileChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      setSelectedFile(file);
      setPreviewUrl(URL.createObjectURL(file));
    }
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    setSuccessMessage("");
    setErrorUpdate("");
    
    const dataToSend = new FormData();
    dataToSend.append('idUser', formData.idUser);
    dataToSend.append('prenom', formData.prenom.trim().replace(/['";]/g, ""));
    dataToSend.append('nom', formData.nom.trim().replace(/['";]/g, ""));
    dataToSend.append('identifiant', formData.identifiant.trim().replace(/['";]/g, ""));
    dataToSend.append('mdp', formData.mdp.trim().replace(/['";]/g, ""));
    
    if (selectedFile) {
        dataToSend.append('profileImg', selectedFile);
    }

    try {
      setLoading(true);
      const res = await fetch(`${API_URL}/maj/admin/update-profile`, {
        method: "PUT",
        credentials: "include",
        body: dataToSend
      });
      
      const data = await res.json();

      if (res.ok && data.success) {
        setSuccessMessage("Votre profil a été mis à jour avec succès ! ✨");
        if (data.linkImg) {
            setAdminInfo(prev => ({ ...prev, linkImg: data.linkImg }));
            setSelectedFile(null);
            setPreviewUrl(null);
        }
      } else {
        if (res.status === 401) {
            setErrorUpdate("Votre session a expiré. Veuillez vous reconnecter.");
        } else {
            setErrorUpdate(data.message || "Une erreur s'est produite lors de la sauvegarde.");
        }
      }
    } catch (err) {
      setErrorUpdate("Impossible de contacter le serveur. Vérifiez votre connexion.");
    } finally {
      setLoading(false);
    }
  };

  if (loading && !adminInfo) return (
    <div className="text-center py-5">
      <div className="spinner-border text-primary" role="status"></div>
      <p className="mt-2 text-muted small">Accès à vos paramètres...</p>
    </div>
  );

  return (
    <div className="container-fluid py-4 animate__animated animate__fadeIn">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h2 className="fw-bold outfit text-dark">⚙️ Paramètres du Profil</h2>
      </div>

      <div className="row">
        <div className="col-lg-8 mx-auto">
          <div className="card-custom border-0 shadow-sm rounded-4 overflow-hidden bg-white">
            <div className="bg-primary p-4 text-white text-center">
              <div className="position-relative d-inline-block">
                <img 
                    src={previewUrl || adminInfo?.linkImg || `https://ui-avatars.com/api/?name=${formData.prenom}+${formData.nom}&background=fff&color=1700E2&size=100`} 
                    alt="avatar" 
                    className="rounded-circle border border-4 border-white border-opacity-25 shadow"
                    width="120"
                    height="120"
                    style={{ objectFit: 'cover' }}
                />
                <button 
                    type="button"
                    onClick={() => fileInputRef.current.click()}
                    className="btn btn-sm btn-light border-0 rounded-circle position-absolute bottom-0 end-0 shadow-lg" 
                    style={{ width: '36px', height: '36px', padding: 0 }}
                >
                    <i className="bi bi-camera-fill text-primary"></i>
                </button>
                <input 
                    type="file" 
                    ref={fileInputRef} 
                    className="d-none" 
                    accept="image/*" 
                    onChange={handleFileChange}
                />
              </div>
              <h4 className="mt-3 fw-bold outfit mb-0 text-white">{formData.prenom} {formData.nom}</h4>
              <p className="small text-white text-opacity-75 mb-0">Administrateur Système</p>
            </div>

            <div className="p-4 p-md-5">
              {(error || errorUpdate) && (
                <div className="alert alert-danger px-4 py-3 rounded-4 small mb-4">
                  <i className="bi bi-exclamation-circle-fill me-2"></i> {error || errorUpdate}
                </div>
              )}
              {successMessage && (
                <div className="alert alert-success px-4 py-3 rounded-4 small mb-4">
                  <i className="bi bi-check-circle-fill me-2"></i> {successMessage}
                </div>
              )}

              <form onSubmit={handleUpdate} className="row g-3">
                <div className="col-md-6 mb-3">
                  <label className="text-secondary small fw-bold mb-2 text-uppercase">Prénom</label>
                  <input 
                    type="text" 
                    name="prenom" 
                    className="form-control-custom" 
                    value={formData.prenom} 
                    onChange={handleChange} 
                    placeholder="Votre prénom" 
                    required
                  />
                </div>
                <div className="col-md-6 mb-3">
                  <label className="text-secondary small fw-bold mb-2 text-uppercase">Nom</label>
                  <input 
                    type="text" 
                    name="nom" 
                    className="form-control-custom" 
                    value={formData.nom} 
                    onChange={handleChange} 
                    placeholder="Votre nom" 
                    required
                  />
                </div>
                <div className="col-md-6 mb-3">
                  <label className="text-secondary small fw-bold mb-2 text-uppercase">Identifiant</label>
                  <input 
                    type="text" 
                    name="identifiant" 
                    className="form-control-custom" 
                    value={formData.identifiant} 
                    onChange={handleChange} 
                    placeholder="Nom d'utilisateur" 
                    required
                  />
                </div>
                <div className="col-md-6 mb-4">
                  <label className="text-secondary small fw-bold mb-2 text-uppercase">Mot de passe</label>
                  <div className="position-relative">
                    <input 
                      type={showPassword ? "text" : "password"} 
                      name="mdp" 
                      className="form-control-custom" 
                      value={formData.mdp} 
                      onChange={handleChange} 
                      placeholder="••••••••" 
                      required
                      style={{ paddingRight: '45px' }}
                    />
                    <button 
                        type="button"
                        className="position-absolute end-0 top-50 translate-middle-y border-0 bg-transparent pe-3 text-muted"
                        onClick={() => setShowPassword(!showPassword)}
                    >
                        <i className={`bi ${showPassword ? 'bi-eye-slash-fill' : 'bi-eye-fill'}`}></i>
                    </button>
                  </div>
                </div>

                <div className="col-12 pt-3">
                  <button type="submit" disabled={loading} className="btn-primary-custom w-100 py-3 rounded-4 fw-bold shadow-sm d-flex align-items-center justify-content-center gap-2">
                    {loading ? <div className="spinner-border spinner-border-sm"></div> : <i className="bi bi-check2-circle fs-5"></i>}
                    ENREGISTRER LES MODIFICATIONS
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Parametres;